import { AxiosError } from "axios";
import React, { FC, useState } from "react";
import { FileQuestion } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useUpdateEffect } from "react-use";
import { FormCard } from "src/components";
import { Button } from "src/components/ui/button";
import useAdminForm from "src/hooks/admin/useAdminForm/useAdminForm";
import useArchiveForm from "src/hooks/admin/useArchiveForm/useArchiveForm";
import formatQuestions from "../EditObservationQuestionsForrm/formatQuestions";
import useDuplicateForm from "src/hooks/admin/useDuplicateForm/useDuplicateForm";
import { FormPreviewModal } from "src/modals";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import { usePatchObservationForm } from "src/features/observationsAdmin/hooks";

interface Props {
	showCardOptions?: boolean;
	data?: IAdminObservationForm[];
	isError: boolean;
	isLoading: boolean;
	isSuccess: boolean;
	error: unknown;
	refetch: () => void;
	status: string;
}

const AdminForms: FC<Props> = ({
	showCardOptions = false,
	data: forms,
	isError,
	error,
	isLoading,
	isSuccess,
	status,
	refetch
}) => {
	const navigate = useNavigate();

	const [selectedForm, setSelectedForm] = useState<{
		formId?: number;
		action?: "ARCHIVE" | "DUPLICATE";
	}>({ formId: undefined, action: undefined });

	const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
	const [isPreviewFormModalOpen, setIsPreviewFormModalOpen] = useState(false);

	const { data: adminForm } = useAdminForm(selectedForm.formId, {
		enabled: Boolean(selectedForm.formId)
	});

	const { mutate: duplicateForm, isLoading: isDuplicatingForm } =
		useDuplicateForm();

	const { mutate: archiveForm, isLoading: isArchivingForm } = useArchiveForm();

	const { mutate: updateForm } = usePatchObservationForm();

	useUpdateEffect(() => {
		if (selectedForm.action) {
			return setIsConfirmationModalOpen(true);
		}

		setIsPreviewFormModalOpen(true);
	}, [selectedForm]);

	const renderFormCard = (form: any) => {
		const { id, name, observationType, updatedBy, updatedAt } = form;

		return (
			<FormCard
				key={id}
				showCardOptions={showCardOptions}
				name={name}
				observationType={observationType}
				updatedAt={updatedAt}
				updatedBy={updatedBy}
				isFormPublished={form.published}
				onMoveToDraftsClick={(e) => {
					e.preventDefault();
					e.stopPropagation();

					updateForm({ id, published: false, draft: true });
				}}
				onPublishFormClick={(e) => {
					e.preventDefault();
					e.stopPropagation();

					updateForm({ id, published: true, draft: false });
				}}
				onSettingsClick={(e) => {
					e.preventDefault();
					e.stopPropagation();

					navigate(`/observations/forms/${id}/settings/`);
				}}
				onEditFormClick={(e) => {
					e.preventDefault();
					e.stopPropagation();

					navigate(`/observations/forms/${id}/questions/`);
				}}
				onArchiveFormClick={(e) => {
					e.preventDefault();
					e.stopPropagation();

					setSelectedForm({ formId: id, action: "ARCHIVE" });
				}}
				onDuplicateFormClick={(e) => {
					e.preventDefault();
					e.stopPropagation();

					setSelectedForm({ formId: id, action: "DUPLICATE" });
				}}
			/>
		);
	};

	const isFormsDataEmpty = isSuccess && forms && forms.length === 0;

	const handleSubmitConfirmationModalPress = () => {
		const { formId, action } = selectedForm;

		if (!formId || !action) return;

		if (action === "DUPLICATE") {
			return duplicateForm(formId, {
				onSettled: () => setIsConfirmationModalOpen(false)
			});
		}

		if (action === "ARCHIVE") {
			return archiveForm(
				{
					id: formId,
					draft: false,
					published: false,
					archived: true
				},
				{
					onSettled: () => setIsConfirmationModalOpen(false),
					onSuccess: refetch
				}
			);
		}
	};

	return (
		<>
			{isError && error && (
				<p data-testid="published-forms-errormsg">
					{(error as AxiosError).message}
				</p>
			)}

			{isLoading && (
				<div className="flex justify-center items-center">
					<div
						className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin"
						aria-label="Loading..."
						data-testid="published-forms-loading"
					/>
				</div>
			)}

			{isSuccess && forms && forms.length === 0 && (
				<div className="flex flex-col items-center justify-center py-16 text-center">
					<p className="text-muted-foreground text-sm">
						No forms in this category yet. Click &quot;New Form&quot; to create
						one.
					</p>
				</div>
			)}

			{isSuccess && forms && forms.length > 0 && (
				<div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
					{forms.map((form) => (
						<React.Fragment key={form.id}>
							{status === "published" ? (
								<button
									type="button"
									className="text-left w-full bg-transparent border-0 p-0 cursor-pointer"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();

										setSelectedForm({ formId: form.id, action: undefined });
									}}
								>
									{renderFormCard(form)}
								</button>
							) : (
								<Link to={`/observations/forms/${form.id}/questions/`}>
									{renderFormCard(form)}
								</Link>
							)}
						</React.Fragment>
					))}
				</div>
			)}

			{selectedForm.formId && selectedForm.action && (
				<ConfirmationModal
					isOpen={isConfirmationModalOpen}
					description={`Are you sure you want to ${selectedForm.action?.toLowerCase()} this
							form?`}
					onCancel={() => setIsConfirmationModalOpen(false)}
					onSubmit={handleSubmitConfirmationModalPress}
					submitBtnProps={{
						children: selectedForm.action === "DUPLICATE" ? "Duplicate" : "Yes",
						isLoading: isDuplicatingForm || isArchivingForm
					}}
					icon={
						selectedForm.action === "DUPLICATE" ? (
							<FileQuestion size={56} />
						) : undefined
					}
				/>
			)}

			{status === "published" && adminForm && (
				<FormPreviewModal
					isOpen={isPreviewFormModalOpen}
					onClose={() => setIsPreviewFormModalOpen(false)}
					formName={adminForm.name}
					hideButtons={true}
					questions={formatQuestions(adminForm) as any}
				/>
			)}
		</>
	);
};

export default AdminForms;
